'use client'

import { useCart } from '@shopify/hydrogen-react'
import { useEffect, useRef } from 'react'

import { env } from '@/env'
import { useAnalyzify } from 'analyzify-js/react'

import { toStorefrontCart } from './Analyzify.adapters'
import { isAnalyzifyEnabled } from './Analyzify.constants'

import type { CartLine } from '@shopify/hydrogen-react/storefront-api-types'

/**
 * Keeps the SDK's view of the cart current.
 *
 * The SDK attaches the cart id to every event and writes Analyzify's attribution
 * attributes onto the cart, which is how an order is attributed back to the
 * session. track.addToCart and friends sync the cart themselves, so this covers
 * the first load and any change we do not track.
 *
 * The SDK ships useCartSync() for this, but it dedupes on
 * `id|totalQuantity|updatedAt` and useCart() exposes no updatedAt, so a change
 * that leaves the total quantity alone would be missed.
 */
const Sync = () => {
  const cart = useCart()
  const { setCart } = useAnalyzify()
  const synced = useRef<string | null>(null)
  const hadCart = useRef(false)

  const status = cart?.status
  const cartId = cart?.id ?? ''
  const lines = (cart?.lines ?? []) as CartLine[]
  const fingerprint = `${cartId}|${lines.map((line) => `${line?.id}:${line?.quantity}`).join(',')}`

  useEffect(() => {
    // An in-flight cart still holds the pre-change lines.
    if (status !== 'idle') return

    if (cartId) {
      if (synced.current === fingerprint) return

      synced.current = fingerprint
      hadCart.current = true
      setCart(toStorefrontCart(cart))

      return
    }

    // Only clear a cart the SDK was actually told about; a cart that has not
    // loaded yet is not a cart that went away.
    if (!hadCart.current) return

    hadCart.current = false
    synced.current = null
    setCart(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, cartId, fingerprint])

  return null
}

/** Mirrors the cart into the SDK. Renders nothing. */
export const AnalyzifyCartSync = () => {
  if (!isAnalyzifyEnabled(env.NEXT_PUBLIC_ANALYZIFY_MODE)) return null

  return <Sync />
}
